import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { lastValueFrom } from "rxjs";
import { OAuthService } from "angular-oauth2-oidc";
import { environment } from 'src/environments/environment';
import { NotificationService } from "./notification.service";

@Injectable({
    providedIn: 'root'
})
export class HTTP {
    private static _instance: HTTP | null = null;
    private debug = signal(false);
    private refreshInFlight: Promise<void> | null = null;
    private static get instance(): HTTP {
        return this._instance ??= new HTTP();
    }
    
    private http = inject(HttpClient);
    // private headers = inject(AppService).getAuthHeaders();
    public readonly baseUrl = environment.apiUrl;
    private oauth = inject(OAuthService);
    private notificationService = inject(NotificationService);
    private constructor() {}    

    public getHeaders(headers?: HttpHeaders | Record<string, string>): HttpHeaders {
        const token = this.oauth.getAccessToken();
        if (headers instanceof HttpHeaders) return headers;
        
        let httpHeaders = new HttpHeaders({
            'Authorization': `Bearer ${token}`,
        });
        
        Object.keys(headers ??= {}).forEach(key => {
            httpHeaders = httpHeaders.append(key, (headers as Record<string, string>)[key]);
        });
        return httpHeaders;
    }

    static async get<T = any>(endpoint: string, headers?: HttpHeaders | Record<string, string>, responseType: ResponseType = 'json', handled = false): Promise<T> {
        const url = join(this.instance.baseUrl, 'api', endpoint);
        let options: { headers: HttpHeaders; responseType: any } | undefined;
        const request = () => {
            options = { headers: this.instance.getHeaders(headers), responseType: responseType as any };
            return lastValueFrom(this.instance.http.get<T>(url, options));
        };
        let result: T | undefined;
        try {
            result = await this.instance.withTokenRefreshRetry(request);
            return result;
        } catch (error) {
            if (handled) throw error;
            if ("status" in (error as any) && (error as any).status === 401) {
                throw error;
            }
            const {summary, detail} = parseErrorResponse(error);
            this.instance.notificationService.error({
                summary: summary ?? 'Fehler',
                detail: detail ?? 'Daten konnten nicht abgerufen werden.',
                lifetime: 5000,
                sticky: true
            });
            throw error;
        } finally {
            this.debugLog('get', url, options, result);
        }
    }
    
    static async post<T = any>(endpoint: string, body: any, headers?: HttpHeaders | Record<string, string>, handled = false): Promise<T> {
        const url = join(this.instance.baseUrl, 'api', endpoint);
        let options: { headers: HttpHeaders } | undefined;
        const request = () => {
            options = { headers: this.instance.getHeaders(headers) };
            return lastValueFrom(this.instance.http.post<T>(url, body, options));
        };
        let result: T | undefined;
        try {
            result = await this.instance.withTokenRefreshRetry(request);
            return result;
        } catch (error) {
            if (handled) throw error;
            const {summary, detail} = parseErrorResponse(error);
            this.instance.notificationService.error({
                summary: summary ?? 'Fehler',
                detail: detail ?? 'Daten konnten nicht gesendet werden.',
                lifetime: 5000,
                sticky: true
            });
            throw error;
        } finally {
            this.debugLog('post', url, options, result);
        }
    }

    /**
     * Wie `post`, meldet aber unterwegs den Fortschritt.
     *
     * Fuer grosse Dateien: ohne Rueckmeldung sieht ein Upload aus wie ein haengender Knopf, und der
     * zweite Klick schickt ihn ein zweites Mal. `onProgress` bekommt 0..100, sobald der Browser
     * die Gesamtgroesse kennt -- bei einem Server ohne Content-Length bleibt sie aus, deshalb ist
     * der Parameter optional und der Aufrufer darf sich nicht darauf verlassen.
     *
     * Bewusst ohne die Token-Wiederholung von `post`: einen 230-MB-Koerper nach abgelaufenem Token
     * ein zweites Mal hochzuladen waere teurer als die Anmeldung vorher zu erneuern.
     */
    static async postWithProgress<T = any>(endpoint: string, body: FormData,
                                           onProgress?: (percent: number) => void,
                                           headers?: HttpHeaders | Record<string, string>): Promise<T> {
        const url = join(this.instance.baseUrl, 'api', endpoint);
        const options = { headers: this.instance.getHeaders(headers), reportProgress: true,
                          observe: 'events' as const };
        return new Promise<T>((resolve, reject) => {
            this.instance.http.post<T>(url, body, options).subscribe({
                next: (event: any) => {
                    if (event.type === HttpEventType.UploadProgress && event.total) {
                        onProgress?.(Math.round((event.loaded / event.total) * 100));
                    } else if (event.type === HttpEventType.Response) {
                        resolve(event.body as T);
                    }
                },
                error: (fehler) => reject(fehler),
            });
        });
    }

    static async put<T = any>(endpoint: string, body: any, headers?: HttpHeaders | Record<string, string>, handled = false): Promise<T> {
        const url = join(this.instance.baseUrl, 'api', endpoint);
        let options: { headers: HttpHeaders } | undefined;
        const request = () => {
            options = { headers: this.instance.getHeaders(headers) };
            return lastValueFrom(this.instance.http.put<T>(url, body, options));
        };
        let result: T | undefined;
        try {
            result = await this.instance.withTokenRefreshRetry(request);
            return result;
        } catch (error) {
            if (handled) throw error;
            const {summary, detail} = parseErrorResponse(error);
            this.instance.notificationService.error({
                summary: summary ?? 'Fehler',
                detail: detail ?? 'Daten konnten nicht aktualisiert werden.',
                lifetime: 5000,
                sticky: true
            });
            throw error;
        } finally {
            this.debugLog('put', url, options, result);
        }
    }

    static async delete<T = any>(endpoint: string, params?: any, headers?: HttpHeaders | Record<string, string>, handled = false): Promise<T> {
        const url = join(this.instance.baseUrl, 'api', endpoint);
        let options: { headers: HttpHeaders; params: any } | undefined;
        const request = () => {
            options = { headers: this.instance.getHeaders(headers), params };
            return lastValueFrom(this.instance.http.delete<T>(url, options));
        };
        let result: T | undefined;
        try {
            result = await this.instance.withTokenRefreshRetry(request);
            return result;
        } catch (error) {
            if (handled) throw error;
            const {summary, detail} = parseErrorResponse(error);
            this.instance.notificationService.error({
                summary: summary ?? 'Fehler',
                detail: detail ?? 'Daten konnten nicht gelöscht werden.',
                lifetime: 5000,
                sticky: true
            });
            throw error;
        } finally {
            this.debugLog('delete', url, options, result);
        }
    }

    static setDebug(debug: boolean) {
        this.instance.debug.set(debug);
    }

    private static debugLog(method: string, url: string, options?: any, result?: any) {
        if (!this.instance.debug()) return;
        console.log(`[HTTP] ${method.toUpperCase()} ${url}`, options, result);
    }

    
    private async withTokenRefreshRetry<T>(request: () => Promise<T>, retried = false): Promise<T> {
        try {
            return await request();
        } catch (error) {
            if (!retried && this.shouldRefreshToken(error)) {
                await this.refreshAccessToken();
                return this.withTokenRefreshRetry(request, true);
            }
            throw error;
        }
    }

    private shouldRefreshToken(error: unknown): boolean {
        if (!(error instanceof HttpErrorResponse)) return false;
        if (error.status !== 401) return false;
        const headers = error.headers;
        if (headers.has('X-Token-Refresh-Required') || headers.has('x-token-refresh-required') || error.error?.code === "TOKEN_STALE") {
            return true;
        }
        // Ein schlicht abgelaufenes Access-Token ist der haeufigste 401 ueberhaupt: authentik gibt
        // dem tickets-Provider 5 Minuten Gueltigkeit, und services/session.py antwortet darauf mit
        // einem gewoehnlichen {"error": "invalid token"} - ohne X-Token-Refresh-Required und ohne
        // TOKEN_STALE. Ohne diesen Zweig lief ab Minute 5 jeder Request in 401, bis sich der
        // Benutzer neu anmeldete. Nur versuchen, wenn ueberhaupt ein Refresh-Token vorliegt, sonst
        // schickt ein nie angemeldeter Aufrufer eine sinnlose Anfrage an den Token-Endpunkt.
        return !!this.oauth.getRefreshToken();
    }

    private async refreshAccessToken(): Promise<void> {
        if (this.refreshInFlight) {
            await this.refreshInFlight;
            return;
        }

        this.refreshInFlight = this.ensureDiscoveryDocumentLoaded()
            .then(() => this.oauth.refreshToken())
            .catch((error) => {
                if (this.isInvalidGrantError(error)) {
                    this.oauth.logOut();
                }
                throw error;
            })
            .then(() => undefined)
            .finally(() => {
            this.refreshInFlight = null;
        });

        await this.refreshInFlight;
    }

    private async ensureDiscoveryDocumentLoaded(): Promise<void> {
        const tokenEndpoint = (this.oauth as any).tokenEndpoint as string | null | undefined;
        if (tokenEndpoint) return;
        await this.oauth.loadDiscoveryDocument();
    }

    private isInvalidGrantError(error: unknown): boolean {
        if (error instanceof HttpErrorResponse) {
            return error.error?.error === 'invalid_grant';
        }

        if (typeof error === 'object' && error !== null) {
            const maybeError = error as {
                error?: string;
                reason?: {
                    error?: {
                        error?: string;
                    };
                };
            };
            return maybeError.error === 'invalid_grant' || maybeError.reason?.error?.error === 'invalid_grant';
        }

        return false;
    }
}

type ResponseType = 'json' | 'blob' | 'text' | 'arraybuffer' | 'document';

function join(...parts: string[]): string {
    return parts.map((part, index) => {
        if (index === 0) return part.replace(/\/+$/, ''); // Remove trailing slashes from the first part
        return part.replace(/^\/+|\/+$/g, ''); // Remove leading and trailing slashes from other parts
    }).filter(part => part.length > 0).join('/');
}

type Violation = {
    type: string;
    expected?: string;
};

type BackendErrorPayload = {
    error?: {
        code?: string;
        message?: string;
        request_id?: string;
        details?: {
            violations?: Record<string, Violation[]>;
        };
    };
};

function parseErrorResponse(error: unknown): {summary: string | null, detail: string | null} {
    if (!(error instanceof HttpErrorResponse)) {
        return {summary: null, detail: null};   
    }

    const payload = error as BackendErrorPayload;
    const backendError = payload?.error;
    if (!backendError) {
        return {summary: null, detail: null};
    }

    if (backendError.code === 'VALIDATION_ERROR') {
        const violations = backendError.details?.violations ?? {};
        return {summary: 'Validierungsfehler', detail: Object.entries(violations).map(([key, v]) => {
            const errors = v.map(violation => `${violation.type}${violation.expected ? ` (erwartet: ${violation.expected})` : ''}`).join(', ');
            return `${key}: ${errors}`;
        }).join('\n')};
    }
    if (backendError.message && backendError.request_id) {
        return {summary: 'Fehler', detail: `${backendError.message} (Request ID: ${backendError.request_id})`};
    }

    return {summary: 'Fehler', detail: backendError.message ?? null};
}